import React from 'react';
import { X, TrendingUp } from 'lucide-react';

interface InvestmentTier {
  name: string;
  minBtc: number;
  maxBtc: number;
  minPercent: number;
  maxPercent: number;
  color: string;
}

interface InvestmentTiersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvestNow: () => void;
}

const InvestmentTiersModal: React.FC<InvestmentTiersModalProps> = ({
  isOpen,
  onClose,
  onInvestNow
}) => {
  const investmentTiers: InvestmentTier[] = [
    { name: 'SHRIMP', minBtc: 0.01, maxBtc: 0.01, minPercent: 0.2, maxPercent: 0.5, color: 'from-gray-600 to-gray-700' },
    { name: 'CRAB', minBtc: 0.1, maxBtc: 0.1, minPercent: 0.35, maxPercent: 0.7, color: 'from-orange-600 to-orange-700' },
    { name: 'OCTOPUS', minBtc: 1, maxBtc: 1, minPercent: 0.6, maxPercent: 1.0, color: 'from-purple-600 to-purple-700' },
    { name: 'DOLPHIN', minBtc: 5, maxBtc: 5, minPercent: 0.8, maxPercent: 1.3, color: 'from-blue-600 to-blue-700' },
    { name: 'SHARK', minBtc: 10, maxBtc: 10, minPercent: 1.2, maxPercent: 2.6, color: 'from-red-600 to-red-700' },
    { name: 'WHALE', minBtc: 50, maxBtc: 50, minPercent: 2.1, maxPercent: 4.2, color: 'from-green-600 to-green-700' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Investment Tiers</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">Invest More. Earn More.</h3>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              You want to make higher profit? No problem! Depending on your investment you are able to 
              trade with a certain leverage. The higher your leverage, the higher your daily profit.
            </p>
          </div>

          {/* Investment Tiers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {investmentTiers.map((tier, index) => (
              <div key={index} className="bg-slate-700/50 rounded-lg p-6 border border-slate-600 hover:border-slate-500 transition-colors">
                <div className="text-center">
                  <div className={`w-16 h-16 bg-gradient-to-r ${tier.color} rounded-lg mx-auto mb-4 flex items-center justify-center`}>
                    <span className="text-white font-bold text-lg">{tier.name.charAt(0)}</span>
                  </div>
                  
                  <h4 className="text-slate-300 font-semibold mb-2">{tier.name}</h4>
                  
                  <div className="text-3xl font-bold text-white mb-4">
                    {tier.minPercent} - {tier.maxPercent}%
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg py-3 px-4">
                    <div className="text-slate-400 text-sm mb-1">Minimum Investment</div>
                    <div className="text-white font-bold">{tier.minBtc} BTC</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                onInvestNow();
                onClose();
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
            >
              Invest Now
            </button>
            <button
              onClick={onClose}
              className="bg-slate-600 hover:bg-slate-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
            >
              Return
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-8 bg-slate-700/30 rounded-lg p-4">
            <div className="text-center text-slate-400 text-sm">
              <p className="mb-2">
                <strong className="text-white">Daily Profit Calculation:</strong> Profits are calculated based on your allocated balance and tier.
              </p>
              <p>
                The robot executes approximately 720 trades per day (1 trade every 2 minutes) to achieve your tier's daily profit target.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentTiersModal;